import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';

// ─── Checklist de itens ───────────────────────────────────────────────────────
const GRUPOS = [
  {
    grupo: 'Documentos',
    itens: ['CRLV no veículo', 'Manual do proprietário', 'Chave reserva'],
  },
  {
    grupo: 'Lataria',
    itens: [
      'Porta dianteira esquerda',
      'Porta dianteira direita',
      'Porta traseira esquerda',
      'Porta traseira direita',
      'Capô',
      'Tampa do porta-malas',
      'Para-choque dianteiro',
      'Para-choque traseiro',
      'Retrovisor esquerdo',
      'Retrovisor direito',
    ],
  },
  {
    grupo: 'Vidros',
    itens: [
      'Para-brisa dianteiro',
      'Vidro dianteiro esquerdo',
      'Vidro dianteiro direito',
      'Vidro traseiro esquerdo',
      'Vidro traseiro direito',
      'Vidro traseiro (luneta)',
    ],
  },
  {
    grupo: 'Pneus e Rodas',
    itens: ['Roda dianteira esquerda', 'Roda dianteira direita', 'Roda traseira esquerda', 'Roda traseira direita', 'Estepe presente'],
  },
  {
    grupo: 'Luzes',
    itens: ['Faróis dianteiros', 'Lanternas traseiras', 'Luz de freio', 'Luz de ré', 'Setas / Pisca'],
  },
  {
    grupo: 'Interior',
    itens: ['Tapetes presentes', 'Banco sem rasgos', 'Cinto de segurança', 'Rádio / Som', 'Ar condicionado'],
  },
  {
    grupo: 'Itens Adicionais',
    itens: ['Triângulo presente', 'Macaco e chave de roda', 'Extintor presente'],
  },
];

const STATUS_ITEM = {
  ok: { label: 'OK', cls: 'bg-green-500 text-white' },
  avariado: { label: 'Avariado', cls: 'bg-red-500 text-white' },
  ausente: { label: 'Ausente', cls: 'bg-gray-400 text-white' },
};

// ─── SVG do carro (vista superior) ────────────────────────────────────────────
function CarDiagram({ marcadores, onAddMarcador, onRemoveMarcador }) {
  const svgRef = useRef(null);

  const handleClick = e => {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    // Coordenadas relativas ao SVG em porcentagem
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onAddMarcador({ id: Date.now(), x, y, nota: '' });
  };

  return (
    <div className="relative select-none">
      <svg
        ref={svgRef}
        viewBox="0 0 200 400"
        className="w-full max-w-xs mx-auto cursor-crosshair"
        onClick={handleClick}
        style={{ touchAction: 'none' }}
        onTouchEnd={e => {
          e.preventDefault();
          const t = e.changedTouches[0];
          handleClick({ clientX: t.clientX, clientY: t.clientY });
        }}
      >
        {/* Corpo do carro */}
        <rect x="30" y="60" width="140" height="280" rx="30" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="2" />
        {/* Capô */}
        <rect x="45" y="60" width="110" height="80" rx="10" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5" />
        {/* Para-brisa dianteiro */}
        <rect x="55" y="110" width="90" height="40" rx="6" fill="#bae6fd" stroke="#7dd3fc" strokeWidth="1.5" opacity="0.8" />
        {/* Teto */}
        <rect x="50" y="150" width="100" height="100" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5" />
        {/* Para-brisa traseiro */}
        <rect x="55" y="250" width="90" height="40" rx="6" fill="#bae6fd" stroke="#7dd3fc" strokeWidth="1.5" opacity="0.8" />
        {/* Porta-malas */}
        <rect x="45" y="260" width="110" height="80" rx="10" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5" />
        {/* Pneus dianteiros */}
        <rect x="8" y="80" width="26" height="50" rx="8" fill="#475569" />
        <rect x="166" y="80" width="26" height="50" rx="8" fill="#475569" />
        {/* Pneus traseiros */}
        <rect x="8" y="270" width="26" height="50" rx="8" fill="#475569" />
        <rect x="166" y="270" width="26" height="50" rx="8" fill="#475569" />
        {/* Portas - linhas divisórias */}
        <line x1="30" y1="200" x2="170" y2="200" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,3" />
        {/* Espelhos */}
        <ellipse cx="24" cy="145" rx="10" ry="6" fill="#94a3b8" />
        <ellipse cx="176" cy="145" rx="10" ry="6" fill="#94a3b8" />
        {/* Faróis */}
        <rect x="48" y="62" width="35" height="14" rx="4" fill="#fde68a" stroke="#fbbf24" strokeWidth="1" />
        <rect x="117" y="62" width="35" height="14" rx="4" fill="#fde68a" stroke="#fbbf24" strokeWidth="1" />
        {/* Lanternas traseiras */}
        <rect x="48" y="324" width="35" height="14" rx="4" fill="#fca5a5" stroke="#f87171" strokeWidth="1" />
        <rect x="117" y="324" width="35" height="14" rx="4" fill="#fca5a5" stroke="#f87171" strokeWidth="1" />
        {/* Label frente/trás */}
        <text x="100" y="45" textAnchor="middle" fontSize="11" fill="#64748b" fontWeight="600">FRENTE</text>
        <text x="100" y="395" textAnchor="middle" fontSize="11" fill="#64748b" fontWeight="600">TRASEIRA</text>

        {/* Marcadores de dano */}
        {marcadores.map(m => (
          <g key={m.id}>
            <circle
              cx={m.x * 2}
              cy={m.y * 4}
              r="9"
              fill="#ef4444"
              stroke="white"
              strokeWidth="2"
              style={{ cursor: 'pointer' }}
              onClick={e => { e.stopPropagation(); onRemoveMarcador(m.id); }}
            />
            <text
              x={m.x * 2}
              y={m.y * 4 + 4}
              textAnchor="middle"
              fontSize="10"
              fill="white"
              fontWeight="bold"
              style={{ pointerEvents: 'none' }}
            >
              ×
            </text>
          </g>
        ))}
      </svg>
      <p className="text-center text-xs text-gray-400 mt-1">Toque no carro para marcar danos • Use o botão ✕ na lista para remover</p>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function Checkin() {
  const { servicoId } = useParams();
  const navigate = useNavigate();

  const [servico, setServico] = useState(null);
  const [loading, setLoading] = useState(false);

  // Estado do checkin
  const [km, setKm] = useState('');
  const [combustivel, setCombustivel] = useState(50);
  const [marcadores, setMarcadores] = useState([]);
  const [notaMarcador, setNotaMarcador] = useState({}); // id → texto
  const [checklist, setChecklist] = useState({});
  const [observacoes, setObservacoes] = useState('');
  const [responsavel, setResponsavel] = useState('');

  useEffect(() => {
    api.get(`/servicos/${servicoId}`).then(r => {
      setServico(r.data);
      setKm(r.data.kmEntrada || '');
      setResponsavel(r.data.responsavel || '');
    });
    api.get(`/servicos/${servicoId}/checkin`).then(r => {
      const c = r.data;
      setKm(c.km || '');
      setCombustivel(c.combustivel ?? 50);
      setMarcadores(c.marcadores || []);
      setNotaMarcador(c.notaMarcador || {});
      setChecklist(c.checklist || {});
      setObservacoes(c.observacoes || '');
      setResponsavel(c.responsavel || '');
    }).catch(() => {});
  }, [servicoId]);

  const [gruposAbertos, setGruposAbertos] = useState({});
  const toggleGrupo = grupo => setGruposAbertos(prev => ({ ...prev, [grupo]: !prev[grupo] }));

  const toggleItem = (item, status) => {
    setChecklist(prev => {
      const atual = prev[item];
      return { ...prev, [item]: atual === status ? undefined : status };
    });
  };

  const adicionarMarcador = m => setMarcadores(prev => [...prev, m]);
  const removerMarcador = id => {
    setMarcadores(prev => prev.filter(m => m.id !== id));
    setNotaMarcador(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const salvar = async () => {
    setLoading(true);
    try {
      await api.put(`/servicos/${servicoId}/checkin`, {
        km, combustivel, marcadores, notaMarcador, checklist, observacoes, responsavel,
      });
      if (km) await api.put(`/servicos/${servicoId}`, { kmEntrada: Number(km), responsavel });
      toast.success('Checking salvo! Agora adicione peças e serviços.');
      navigate(`/servicos/${servicoId}?editar=1`);
    } catch {
      toast.error('Erro ao salvar checking');
    } finally {
      setLoading(false);
    }
  };

  if (!servico) return <div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div>;

  const totalMarcadores = marcadores.length;
  const totalItens = Object.keys(checklist).length;
  const itensAvariados = Object.values(checklist).filter(v => v === 'avariado').length;

  return (
    <div className="max-w-2xl mx-auto pb-8">
      <button onClick={() => navigate(`/servicos/${servicoId}`)} className="text-blue-600 text-sm mb-4 hover:underline">← Voltar ao serviço</button>

      {/* Cabeçalho */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Checking do Veículo</h1>
            <p className="text-gray-500 text-sm mt-0.5">Serviço #{servico.numero}</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-gray-800">{servico.veiculo?.placa}</p>
            <p className="text-sm text-gray-500">{servico.veiculo?.marca} {servico.veiculo?.modelo}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">KM de Entrada</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: 85000"
              value={km}
              onChange={e => setKm(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Responsável</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nome do mecânico"
              value={responsavel}
              onChange={e => setResponsavel(e.target.value)}
            />
          </div>
        </div>

        {/* Nível de combustível */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nível de Combustível — <span className="text-blue-600 font-semibold">{combustivel}%</span>
          </label>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">Vazio</span>
            <div className="relative flex-1 h-8 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${combustivel}%`,
                  background: combustivel > 50 ? '#22c55e' : combustivel > 25 ? '#f59e0b' : '#ef4444',
                }}
              />
            </div>
            <span className="text-xs text-gray-400">Cheio</span>
          </div>
          <input
            type="range" min="0" max="100" step="5"
            className="w-full mt-2 accent-blue-600"
            value={combustivel}
            onChange={e => setCombustivel(Number(e.target.value))}
          />
          <div className="flex justify-between text-xs text-gray-400 -mt-1">
            {[0, 25, 50, 75, 100].map(v => <span key={v}>{v}%</span>)}
          </div>
        </div>
      </div>

      {/* Diagrama do carro */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-800">Mapa de Danos</h2>
          <div className="flex items-center gap-3 text-sm">
            {totalMarcadores > 0 && (
              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                {totalMarcadores} dano{totalMarcadores > 1 ? 's' : ''} marcado{totalMarcadores > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        <CarDiagram
          marcadores={marcadores}
          onAddMarcador={adicionarMarcador}
          onRemoveMarcador={removerMarcador}
        />

        {/* Notas por marcador */}
        {marcadores.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-600">Descreva cada dano marcado:</p>
              <button
                type="button"
                onClick={() => { setMarcadores([]); setNotaMarcador({}); }}
                className="text-xs text-red-500 hover:text-red-700 underline"
              >
                Limpar todos
              </button>
            </div>
            {marcadores.map((m, i) => (
              <div key={m.id} className="flex items-center gap-2">
                <span className="w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 font-bold">
                  {i + 1}
                </span>
                <input
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Descrição do dano ${i + 1}...`}
                  value={notaMarcador[m.id] || ''}
                  onChange={e => setNotaMarcador(prev => ({ ...prev, [m.id]: e.target.value }))}
                />
                <button
                  type="button"
                  onClick={() => removerMarcador(m.id)}
                  className="w-8 h-8 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex-shrink-0 transition-colors text-base font-bold"
                  title="Remover dano"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Checklist */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">Vistoria Geral</h2>
          {itensAvariados > 0 && (
            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-sm font-medium">
              {itensAvariados} avaria{itensAvariados > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          {GRUPOS.map(g => {
            const aberto = !!gruposAbertos[g.grupo];
            const marcados = g.itens.filter(item => checklist[item]).length;
            const avarias = g.itens.filter(item => checklist[item] === 'avariado').length;
            return (
            <div key={g.grupo}>
              {/* Cabeçalho clicável do grupo */}
              <button
                type="button"
                onClick={() => toggleGrupo(g.grupo)}
                className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold transition-transform ${aberto ? 'rotate-45' : ''} text-blue-600`}>+</span>
                  <span className="font-semibold text-gray-700">{g.grupo}</span>
                  {marcados > 0 && (
                    <span className="text-xs text-gray-400">{marcados}/{g.itens.length}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {avarias > 0 && (
                    <span className="text-xs font-semibold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                      {avarias} avaria{avarias > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </button>

              {/* Itens do grupo — visíveis só quando aberto */}
              {aberto && (
                <div className="pb-3 space-y-2">
                  {g.itens.map(item => (
                    <div key={item} className="flex items-center justify-between py-1 pl-6">
                      <span className="text-sm text-gray-700 flex-1">{item}</span>
                      <div className="flex gap-1.5">
                        {Object.entries(STATUS_ITEM).map(([status, cfg]) => (
                          <button
                            key={status}
                            type="button"
                            onClick={() => toggleItem(item, status)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              checklist[item] === status
                                ? cfg.cls
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {cfg.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Observações gerais */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Observações Gerais</label>
        <textarea
          rows={4}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Anotações adicionais sobre o estado do veículo, itens do cliente no carro, solicitações especiais..."
          value={observacoes}
          onChange={e => setObservacoes(e.target.value)}
        />
      </div>

      {/* Resumo e salvar */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">Resumo do Checking</p>
        <ul className="space-y-0.5 text-blue-700">
          <li>• {totalMarcadores} ponto{totalMarcadores !== 1 ? 's' : ''} de dano marcado{totalMarcadores !== 1 ? 's' : ''} no diagrama</li>
          <li>• {totalItens} de {GRUPOS.reduce((a, g) => a + g.itens.length, 0)} itens vistoriados</li>
          {itensAvariados > 0 && <li className="text-red-600 font-medium">• {itensAvariados} item{itensAvariados > 1 ? 's' : ''} com avaria</li>}
          <li>• Combustível: {combustivel}%</li>
        </ul>
      </div>

      <button
        onClick={salvar}
        disabled={loading}
        className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-base hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-lg"
      >
        {loading ? 'Salvando...' : '✅ Salvar Checking'}
      </button>
    </div>
  );
}
